import { useState } from 'react'
import html2canvas from 'html2canvas-pro'
import { MAX_SUPPORT_TICKET_DESCRIPTION_LENGTH } from '@storefronts/common-types'
import {
    Dialog,
    DialogCloseButton,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { toast } from '@/components/ui/sonner'
import { Small } from '@/components/ui/typography'
import { R } from '@/modules/api/actions'

const SUPPORT_TICKET_TOPICS = [
    'Account and Users',
    'Brand Center',
    'Bulk Creation',
    'Create with AI',
    'Integrations',
    'Reporting and Data',
    'Shopper Experience',
    'Bugs',
    'General Questions',
] as const

interface ReportIssueModalProps {
    isOpen: boolean
    onClose: () => void
    userEmail: string
    clientName?: string
    shopName?: string
    screenshot: string | null
}

export async function captureViewportScreenshot(): Promise<string | null> {
    try {
        const canvas = await html2canvas(document.body, {
            width: window.innerWidth,
            height: window.innerHeight,
            x: window.scrollX,
            y: window.scrollY,
            scale: 1,
            // useCORS (without allowTaint) skips rendering cross-origin
            // images that lack CORS headers, rather than drawing them and
            // tainting the canvas — a tainted canvas throws on toDataURL().
            useCORS: true,
            logging: false,
        })
        return canvas.toDataURL('image/jpeg', 0.7)
    } catch (error) {
        console.error('Failed to capture screenshot', error)
        return null
    }
}

export const ReportIssueModal: React.FC<ReportIssueModalProps> = ({
    isOpen,
    onClose,
    userEmail,
    clientName,
    shopName,
    screenshot,
}) => {
    const [title, setTitle] = useState('')
    const [description, setDescription] = useState('')
    const [includeScreenshot, setIncludeScreenshot] = useState(true)
    const [isLoading, setIsLoading] = useState(false)
    const [isSubmitted, setIsSubmitted] = useState(false)

    const reset = () => {
        setTitle('')
        setDescription('')
        setIncludeScreenshot(true)
        setIsSubmitted(false)
    }

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            reset()
            onClose()
        }
    }

    const handleSubmit = async () => {
        const trimmedTitle = title.trim()
        const trimmedDescription = description.trim()

        if (!trimmedTitle || !trimmedDescription) {
            toast.error('Please fill in both a title and a description')
            return
        }

        try {
            setIsLoading(true)

            if (includeScreenshot && !screenshot) {
                toast.warning(
                    'Could not capture a screenshot; submitting without it',
                )
            }
            const screenshotToSend =
                includeScreenshot && screenshot ? screenshot : undefined

            const response = await R.createSupportTicket({
                title: trimmedTitle,
                description: trimmedDescription,
                pageUrl: window.location.href,
                screenshot: screenshotToSend,
            })

            if (response.isError) {
                throw new Error(response.message)
            }

            setIsSubmitted(true)
        } catch (error) {
            toast.error(
                error instanceof Error
                    ? error.message
                    : 'Failed to submit your issue',
            )
        } finally {
            setIsLoading(false)
        }
    }

    if (isSubmitted) {
        return (
            <Dialog open={isOpen} onOpenChange={handleOpenChange}>
                <DialogContent className="max-w-md">
                    <DialogCloseButton />
                    <DialogHeader>
                        <DialogTitle>Success!</DialogTitle>
                    </DialogHeader>
                    <DialogDescription className="pb-2 text-base text-foreground">
                        This issue has been reported to the Black Crow AI
                        support team, who will respond via email
                        {userEmail ? ` to ${userEmail}` : ''}.
                    </DialogDescription>
                </DialogContent>
            </Dialog>
        )
    }

    return (
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Report an Issue</DialogTitle>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                    <div className="bg-muted/40 rounded-md border p-3 text-sm text-muted-foreground">
                        <div>
                            <span className="font-medium text-foreground">
                                From:
                            </span>{' '}
                            {userEmail}
                        </div>
                        {clientName && (
                            <div>
                                <span className="font-medium text-foreground">
                                    Client:
                                </span>{' '}
                                {clientName}
                                {shopName ? ` (${shopName})` : ''}
                            </div>
                        )}
                    </div>
                    <div className="space-y-2">
                        <Small className="font-medium text-foreground">
                            Title
                        </Small>
                        <Select value={title} onValueChange={setTitle}>
                            <SelectTrigger className="h-12">
                                <SelectValue placeholder="Select a topic" />
                            </SelectTrigger>
                            <SelectContent>
                                {SUPPORT_TICKET_TOPICS.map((topic) => (
                                    <SelectItem key={topic} value={topic}>
                                        {topic}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <Small className="font-medium text-foreground">
                            Description
                        </Small>
                        <Textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="What happened? What did you expect instead?"
                            maxLength={MAX_SUPPORT_TICKET_DESCRIPTION_LENGTH}
                            rows={5}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="report-issue-include-screenshot"
                            checked={includeScreenshot}
                            onCheckedChange={(checked) =>
                                setIncludeScreenshot(checked === true)
                            }
                        />
                        <Label
                            htmlFor="report-issue-include-screenshot"
                            className="text-sm font-normal"
                        >
                            Include a screenshot of this page
                        </Label>
                    </div>
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => handleOpenChange(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={
                            isLoading || !title.trim() || !description.trim()
                        }
                    >
                        {isLoading ? 'Submitting...' : 'Submit'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
